import { BaseService } from '../base';
import type {
  ListNotificationTypesResponse,
  ListNotificationsParams,
  ListNotificationsResponse,
  MarkAllNotificationsReadResponse,
  UnreadNotificationsCount,
} from './types';

const PORTAL_API_URL = import.meta.env.VITE_API_URL as string | undefined;
const BASE_PATH = '/portal/v1/notifications';

export class NotificationsService extends BaseService {
  async list(params: ListNotificationsParams = {}): Promise<{
    success: boolean;
    data?: ListNotificationsResponse;
    error?: string;
  }> {
    try {
      const query = new URLSearchParams();
      if (params.limit != null) query.append('limit', String(params.limit));
      if (params.cursor) query.append('cursor', params.cursor);
      if (params.type) query.append('type', params.type);
      if (params.status) query.append('status', params.status);
      if (params.partyCode) query.append('partyCode', params.partyCode);

      const suffix = query.toString() ? `?${query.toString()}` : '';
      const response = await this.get(
        `${BASE_PATH}${suffix}`,
        {},
        PORTAL_API_URL
      );
      return await this.handleJsonResponse<ListNotificationsResponse>(response);
    } catch (error) {
      return {
        success: false,
        error: this.describe(error, 'Erro ao carregar as notificações'),
      };
    }
  }

  async getUnreadCount(partyCode?: string): Promise<{
    success: boolean;
    data?: UnreadNotificationsCount;
    error?: string;
  }> {
    try {
      const suffix = partyCode
        ? `?partyCode=${encodeURIComponent(partyCode)}`
        : '';
      const response = await this.get(
        `${BASE_PATH}/unread-count${suffix}`,
        {},
        PORTAL_API_URL
      );
      return await this.handleJsonResponse<UnreadNotificationsCount>(response);
    } catch (error) {
      return {
        success: false,
        error: this.describe(
          error,
          'Erro ao carregar as notificações não lidas'
        ),
      };
    }
  }

  async listTypes(): Promise<{
    success: boolean;
    data?: ListNotificationTypesResponse;
    error?: string;
  }> {
    try {
      const response = await this.get(`${BASE_PATH}/types`, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<ListNotificationTypesResponse>(
        response
      );
    } catch (error) {
      return {
        success: false,
        error: this.describe(error, 'Erro ao carregar os tipos de notificação'),
      };
    }
  }

  /** Idempotente no back: rechamar preserva a data da primeira leitura. */
  async markAsRead(
    notificationId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await this.patch(
        `${BASE_PATH}/${encodeURIComponent(notificationId)}/read`
      );
      return await this.handleJsonResponse<void>(response);
    } catch (error) {
      return {
        success: false,
        error: this.describe(error, 'Erro ao marcar a notificação como lida'),
      };
    }
  }

  async markAllAsRead(): Promise<{
    success: boolean;
    data?: MarkAllNotificationsReadResponse;
    error?: string;
  }> {
    try {
      const response = await this.patch(`${BASE_PATH}/read-all`);
      return await this.handleJsonResponse<MarkAllNotificationsReadResponse>(
        response
      );
    } catch (error) {
      return {
        success: false,
        error: this.describe(
          error,
          'Erro ao marcar as notificações como lidas'
        ),
      };
    }
  }

  /** `BaseService` não expõe PATCH; o verbo vai direto no `fetchWithAuth`. */
  private patch(path: string, body?: unknown): Promise<Response> {
    return this.fetchWithAuth(
      path,
      {
        method: 'PATCH',
        body: body != null ? JSON.stringify(body) : undefined,
      },
      PORTAL_API_URL
    );
  }

  private describe(error: unknown, fallback: string): string {
    return error instanceof Error ? error.message : fallback;
  }
}

export const notificationsService = new NotificationsService();
